import { isCrossOriginApiUrl, resolveApiUrl } from "./resolveApiUrl";

const SAFE_METHODS = new Set(["GET", "HEAD", "OPTIONS"]);
let crossOriginCsrfToken: string | null = null;
let crossOriginCsrfRequest: Promise<string> | null = null;

const readCookie = (name: string): string | null => {
  const entries = document.cookie.split(";").map((item) => item.trim());
  let last: string | null = null;
  for (const entry of entries) {
    if (!entry.startsWith(`${name}=`)) continue;
    last = decodeURIComponent(entry.slice(name.length + 1));
  }
  return last;
};

const syncMetaCsrfToken = (token: string): void => {
  const meta = document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]');
  if (meta) {
    meta.content = token;
    return;
  }
  const head = document.head;
  if (!head) return;
  const el = document.createElement("meta");
  el.name = "csrf-token";
  el.content = token;
  head.prepend(el);
};

export const getCsrfToken = (): string | null => {
  const cookieToken = readCookie("deployher_csrf")?.trim();
  const metaToken = document
    .querySelector<HTMLMetaElement>('meta[name="csrf-token"]')
    ?.content?.trim();
  // Cookie is authoritative for the server; meta is only seeded on the initial HTML shell.
  // After OAuth redirects or cross-subdomain API calls the cookie can diverge from meta.
  if (cookieToken) {
    if (metaToken !== cookieToken) {
      syncMetaCsrfToken(cookieToken);
    }
    return cookieToken;
  }
  return metaToken || null;
};

const getCrossOriginCsrfToken = async (): Promise<string> => {
  if (crossOriginCsrfToken) return crossOriginCsrfToken;
  if (!crossOriginCsrfRequest) {
    crossOriginCsrfRequest = fetch(resolveApiUrl("/api/csrf"), { credentials: "include" })
      .then(async (response) => {
        if (!response.ok) throw new Error(`Could not initialize CSRF protection (${response.status})`);
        const data = (await response.json()) as { csrfToken?: unknown };
        if (typeof data.csrfToken !== "string" || !data.csrfToken.trim()) {
          throw new Error("CSRF endpoint returned an invalid token");
        }
        crossOriginCsrfToken = data.csrfToken.trim();
        return crossOriginCsrfToken;
      })
      .finally(() => {
        crossOriginCsrfRequest = null;
      });
  }
  return crossOriginCsrfRequest;
};

export const fetchWithCsrf = async (
  input: RequestInfo | URL,
  init: RequestInit = {}
): Promise<Response> => {
  const method = (init.method ?? "GET").toUpperCase();
  const headers = new Headers(init.headers ?? {});
  const target = typeof input === "string" || input instanceof URL ? resolveApiUrl(input) : input;
  const crossOrigin = typeof target === "string" && isCrossOriginApiUrl(target);
  if (!SAFE_METHODS.has(method)) {
    const token = crossOrigin ? await getCrossOriginCsrfToken() : getCsrfToken();
    if (token && !headers.has("x-csrf-token")) {
      headers.set("x-csrf-token", token);
    }
  }

  const credentials =
    init.credentials ?? (crossOrigin ? "include" : "same-origin");

  return fetch(target, {
    ...init,
    headers,
    credentials
  });
};
